import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

/**
 * Slice del panel "Live debug" (Admin global). Cada dataset tiene su propio
 * loading/error; el sondeo lo hace la vista. Todas las llamadas son
 * `admin/debug/...` y solo las atiende el backend para Administrador.
 */
const dataset = (initial) => ({ data: initial, isLoading: false, error: null, loadedAt: null });

const initialState = {
  restaurants: dataset([]),
  session: dataset(null),
  presence: dataset([]),
  timeline: dataset([]),
  anomalies: dataset([]),
  clicks: dataset([]),
  action: { ...dataset(null), actionId: null },
};

const errorText = (e) =>
  (typeof e === "string" && e) || e?.message || e?.title || "No se pudo cargar";

const slice = createSlice({
  name: "liveDebug",
  initialState,
  reducers: {
    start(state, { payload: key }) {
      state[key].isLoading = true;
      state[key].error = null;
    },
    fail(state, { payload: { key, error } }) {
      state[key].isLoading = false;
      state[key].error = error;
    },
    set(state, { payload: { key, data } }) {
      state[key].isLoading = false;
      state[key].data = data;
      state[key].loadedAt = Date.now();
    },
    openAction(state, { payload: actionId }) {
      state.action = { ...dataset(null), actionId, isLoading: true };
    },
    closeAction(state) {
      state.action = { ...dataset(null), actionId: null };
    },
    reset() {
      return initialState;
    },
  },
});

export default slice.reducer;
export const { closeAction, reset: resetLiveDebug } = slice.actions;

const load = (key, request, map = (d) => d) => async (dispatch) => {
  dispatch(slice.actions.start(key));
  try {
    const res = await request();
    dispatch(slice.actions.set({ key, data: map(res?.data) }));
    return res?.data;
  } catch (e) {
    dispatch(slice.actions.fail({ key, error: errorText(e) }));
    return null;
  }
};

const iso = (ms) => new Date(ms).toISOString();
const asArray = (d) => (Array.isArray(d) ? d : []);

export const fetchDebugRestaurants = (q = "") =>
  load("restaurants", () => axios.get("/admin/debug/restaurants", { params: { q } }), asArray);

export const fetchDebugSession = (rid) =>
  load("session", () => axios.get(`/admin/debug/${rid}/session`));

export const startDebugSession = (rid, minutes) =>
  load("session", () => axios.post(`/admin/debug/${rid}/session`, { minutes }));

export const stopDebugSession = (rid) => async (dispatch) => {
  dispatch(slice.actions.start("session"));
  try {
    await axios.delete(`/admin/debug/${rid}/session`);
    dispatch(slice.actions.set({ key: "session", data: { active: false, expiresAt: null } }));
  } catch (e) {
    dispatch(slice.actions.fail({ key: "session", error: errorText(e) }));
  }
};

export const fetchDebugPresence = (rid) =>
  load("presence", () => axios.get(`/admin/debug/${rid}/presence`), asArray);

export const fetchDebugTimeline = (rid, { from, to, deviceId, kinds, limit = 5000 }) =>
  load(
    "timeline",
    () =>
      axios.get(`/admin/debug/${rid}/timeline`, {
        params: {
          from: iso(from),
          to: iso(to),
          deviceId: deviceId || undefined,
          kinds: kinds && kinds.length ? kinds.join(",") : undefined,
          limit,
        },
      }),
    asArray,
  );

export const fetchDebugAnomalies = (rid, { from, to, types }) =>
  load(
    "anomalies",
    () =>
      axios.get(`/admin/debug/${rid}/anomalies`, {
        params: { from: iso(from), to: iso(to), types: types && types.length ? types.join(",") : undefined },
      }),
    asArray,
  );

export const fetchDebugClicks = (rid, { screen, from, to }) =>
  load(
    "clicks",
    () => axios.get(`/admin/debug/${rid}/clicks`, { params: { screen, from: iso(from), to: iso(to) } }),
    asArray,
  );

export const fetchDebugAction = (rid, actionId) => async (dispatch) => {
  dispatch(slice.actions.openAction(actionId));
  try {
    const res = await axios.get(`/admin/debug/${rid}/action/${encodeURIComponent(actionId)}`);
    dispatch(slice.actions.set({ key: "action", data: res?.data || null }));
  } catch (e) {
    dispatch(slice.actions.fail({ key: "action", error: errorText(e) }));
  }
};
